import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { Switch } from "@frontend/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@frontend/components/ui/tabs"
import { Textarea } from "@frontend/components/ui/textarea"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import {
  useAddProjectMember,
  useCreateProject,
  useProject,
  useProjectMemberCandidates,
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProject,
  useUpdateProjectMemberRole
} from "@frontend/hooks/use-projects"
import type { ModuleTemplateEditorSubmitPayload } from "@frontend/pages/module-templates/components/module-template-editor-dialog"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, Plus, Trash2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useLocation, useNavigate, useParams } from "react-router"

import { ProjectFormModulesTab } from "./components/modules-tab"
import { type ProjectFormData, projectFormSchema } from "./lib/form-schema"
import {
  formatMode,
  formatProjectRole,
  formatSortDirection,
  formatSortType,
  formatStatus,
  formatWorkflow,
  moduleTypeBadgeClass
} from "./lib/utils"
import type { Member, ModuleTemplate, ProjectModuleItem, ProjectRole } from "./types"

export function ProjectForm() {
  const { projectId } = useParams<{ projectId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = Boolean(projectId)
  const api = useApiClient()

  const initialTab = useMemo<"general" | "modules" | "members">(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get("tab")
    if (tab === "modules") return "modules"
    if (tab === "members") return "members"
    if (location.pathname.endsWith("/members")) return "members"
    return "general"
  }, [location.pathname, location.search])

  const [selectedModuleId, setSelectedModuleId] = useState<string>("")
  const [newTemplateId, setNewTemplateId] = useState("")
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false)
  const [editTemplateOpen, setEditTemplateOpen] = useState(false)
  const [showAddMember, setShowAddMember] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("")
  const [selectedRole, setSelectedRole] = useState<ProjectRole>("annotator")
  const [dependsOnModuleId, setDependsOnModuleId] = useState("")
  const [dependencyMode, setDependencyMode] = useState<"completion_only" | "instance_linked">("completion_only")
  const [dependencyCardinality, setDependencyCardinality] = useState<"one_to_one" | "one_to_many_min1">("one_to_one")
  const [dependencyAutoCreate, setDependencyAutoCreate] = useState(true)
  const [dependencyCascadeDelete, setDependencyCascadeDelete] = useState(true)
  const [dependencyLabelFilter, setDependencyLabelFilter] = useState("")

  const handleTabChange = useCallback(
    (nextTab: "general" | "modules" | "members") => {
      const params = new URLSearchParams(location.search)
      if (nextTab === "general") {
        params.delete("tab")
      } else {
        params.set("tab", nextTab)
      }
      const search = params.toString()
      void navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : ""
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate]
  )

  const { data: existingProject } = useProject(projectId ?? "")
  const { data: currentUser } = useApiQuery<{
    id: string
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")
  const { data: membersResult, isLoading: membersLoading } = useProjectMembers(projectId ?? "")
  const { data: memberCandidatesResult } = useProjectMemberCandidates(projectId ?? "")
  const { data: modulesResult, isLoading: modulesLoading } = useApiQuery<{
    data: ProjectModuleItem[]
  }>(["projects", projectId ?? "", "modules"], `/projects/${projectId}/modules`, {
    enabled: isEdit && Boolean(projectId)
  })
  const { data: templatesResult } = useApiQuery<{
    data: ModuleTemplate[]
  }>(
    ["projects", projectId ?? "", "module-templates", "for-project-settings"],
    `/projects/${projectId}/module-templates?limit=100`,
    { enabled: isEdit && Boolean(projectId) }
  )

  const createProject = useCreateProject()
  const updateProject = useUpdateProject(projectId ?? "")
  const addMember = useAddProjectMember(projectId ?? "")
  const updateRole = useUpdateProjectMemberRole(projectId ?? "")
  const removeMember = useRemoveProjectMember(projectId ?? "")

  const addModule = useMutation({
    mutationFn: (values: { template_id: string }) =>
      api(`/projects/${projectId}/modules`, {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "module-templates", "for-project-settings"]
      })
      setNewTemplateId("")
    }
  })

  const deleteModule = useMutation({
    mutationFn: (moduleId: string) =>
      api(`/projects/${projectId}/modules/${moduleId}`, {
        method: "DELETE"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "module-templates", "for-project-settings"]
      })
    }
  })

  const updateModuleConfig = useMutation({
    mutationFn: (values: { moduleId: string; config: Record<string, unknown> | null }) =>
      api(`/projects/${projectId}/modules/${values.moduleId}`, {
        method: "PATCH",
        body: JSON.stringify({ config: values.config })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
    }
  })

  const createTemplate = useMutation({
    mutationFn: (values: ModuleTemplateEditorSubmitPayload) =>
      api(`/projects/${projectId}/module-templates`, {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: (result: { project_module?: { id: string } }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "module-templates", "for-project-settings"]
      })
      setCreateTemplateOpen(false)
      if (result?.project_module?.id) {
        setSelectedModuleId(result.project_module.id)
      }
    }
  })

  const editTemplate = useMutation({
    mutationFn: (values: { templateId: string; payload: ModuleTemplateEditorSubmitPayload }) =>
      api(`/projects/${projectId}/module-templates/${values.templateId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: values.payload.name,
          schema: values.payload.schema,
          version: values.payload.version
        })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "module-templates", "for-project-settings"]
      })
      setEditTemplateOpen(false)
    }
  })

  const addDependency = useMutation({
    mutationFn: (values: {
      moduleId: string
      depends_on_module_id: string
      dependency_mode: "completion_only" | "instance_linked"
      cardinality: "one_to_one" | "one_to_many_min1"
      auto_create_linked: boolean
      cascade_on_source_delete: boolean
      source_label_names?: string[]
    }) =>
      api(`/projects/${projectId}/modules/${values.moduleId}/dependencies`, {
        method: "POST",
        body: JSON.stringify({
          depends_on_module_id: values.depends_on_module_id,
          dependency_mode: values.dependency_mode,
          cardinality: values.cardinality,
          auto_create_linked: values.auto_create_linked,
          cascade_on_source_delete: values.cascade_on_source_delete,
          source_label_names: values.source_label_names
        })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
      setDependsOnModuleId("")
      setDependencyLabelFilter("")
    }
  })

  const deleteDependency = useMutation({
    mutationFn: (values: { moduleId: string; depId: string }) =>
      api(`/projects/${projectId}/modules/${values.moduleId}/dependencies/${values.depId}`, {
        method: "DELETE"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "modules"]
      })
    }
  })

  const archiveProject = useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/archive`, {
        method: "PATCH"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId!]
      })
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      navigate(`/`)
    }
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty }
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: "",
      description: "",
      mode: "standard",
      workflow: "radiology",
      status: "draft",
      review_enabled: false,
      multiplayer_cursors: false,
      readers_per_case: 1,
      due_date: ""
    }
  })

  const mode = watch("mode")
  const reviewEnabled = watch("review_enabled")
  const projectMembers: Member[] = membersResult?.data ?? []
  const availableMembers: Member[] = memberCandidatesResult?.data ?? []
  const modules = modulesResult?.data ?? []
  const templates = templatesResult?.data ?? []
  const currentUserId = currentUser?.id

  const selectedModule = modules.find((m) => m.id === selectedModuleId) ?? modules[0]
  const selectedTemplate = selectedModule
    ? templates.find((template) => template.id === selectedModule.template_id)
    : undefined

  const isInstanceAdmin = currentUser?.instance_role === "admin"
  const isProjectAdmin = projectMembers.some((m) => m.user_id === currentUserId && m.role === "project_admin")
  const canManageMembers = isInstanceAdmin || isProjectAdmin

  const addableTemplates = templates.filter(
    (template) => !(template.used_in_current_project || modules.some((m) => m.template_id === template.id))
  )
  const availableDependencyTargets = modules.filter((m) => m.id !== selectedModule?.id)

  useEffect(() => {
    if (isEdit && existingProject) {
      reset({
        name: existingProject.name,
        description: existingProject.description ?? "",
        mode: existingProject.mode,
        workflow: existingProject.workflow ?? "radiology",
        status: existingProject.status,
        review_enabled: existingProject.review_enabled === 1,
        multiplayer_cursors: existingProject.multiplayer_cursors === 1,
        readers_per_case: existingProject.readers_per_case,
        sort_metric_key: existingProject.sort_metric_key ?? "",
        sort_metric_type: existingProject.sort_metric_type ?? undefined,
        sort_metric_direction: existingProject.sort_metric_direction ?? undefined,
        due_date: existingProject.due_date ?? ""
      })
    }
  }, [isEdit, existingProject, reset])

  useEffect(() => {
    if (!selectedModuleId && modules.length > 0) {
      setSelectedModuleId(modules[0]!.id)
    }
  }, [modules, selectedModuleId])

  const onSubmit = (data: ProjectFormData) => {
    const payload = {
      ...data,
      description: data.description || null,
      due_date: data.due_date || null,
      sort_metric_key: data.mode === "queued" ? data.sort_metric_key || null : null,
      sort_metric_type: data.mode === "queued" ? data.sort_metric_type || null : null,
      sort_metric_direction: data.mode === "queued" ? data.sort_metric_direction || null : null
    }

    if (isEdit) {
      updateProject.mutate(payload)
    } else {
      createProject.mutate(payload, {
        onSuccess: (result: { id: string }) => {
          navigate(`/projects/${result.id}/edit`)
        }
      })
    }
  }

  const isPending =
    createProject.isPending ||
    updateProject.isPending ||
    addModule.isPending ||
    createTemplate.isPending ||
    editTemplate.isPending ||
    deleteModule.isPending ||
    addDependency.isPending ||
    deleteDependency.isPending

  if (isEdit && !existingProject) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Loading project settings...</p>
      </div>
    )
  }

  return (
    <div className="space-y-5 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/projects/${projectId ?? ""}`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Project Settings</h1>
            {isEdit && <p className="text-sm text-muted-foreground mt-1">{existingProject?.name}</p>}
          </div>
        </div>
        {isEdit && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="capitalize">{existingProject?.status}</span>
          </div>
        )}
      </div>

      {isEdit ? (
        <Tabs
          value={initialTab}
          onValueChange={(v) => handleTabChange(v as "general" | "modules" | "members")}
          className="!flex-col"
        >
          <TabsList className="self-start w-auto h-auto bg-muted/70 rounded-xl p-1 gap-1">
            <TabsTrigger value="general" className="flex-none h-9 px-4 py-1.5 text-sm">
              General
            </TabsTrigger>
            <TabsTrigger value="modules" className="flex-none h-9 px-4 py-1.5 text-sm">
              Modules
            </TabsTrigger>
            <TabsTrigger value="members" className="flex-none h-9 px-4 py-1.5 text-sm">
              Members
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-xl border bg-card/70 p-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-1">
                  <Label htmlFor="name">Project Name</Label>
                  <Input id="name" {...register("name")} aria-invalid={Boolean(errors.name)} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                </div>
                <div className="space-y-2 md:col-span-1">
                  <Label>Status</Label>
                  <Select
                    value={watch("status")}
                    onValueChange={(value) =>
                      setValue("status", value as "draft" | "active" | "paused" | "completed" | "archived", {
                        shouldDirty: true
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue>{formatStatus(watch("status"))}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" rows={3} {...register("description")} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select
                    value={mode}
                    onValueChange={(value) => setValue("mode", value as "standard" | "queued", { shouldDirty: true })}
                  >
                    <SelectTrigger>
                      <SelectValue>{formatMode(mode)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="queued">Queued</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Workflow</Label>
                  <Select
                    value={watch("workflow")}
                    onValueChange={(value) =>
                      setValue("workflow", value as "radiology" | "pathology", {
                        shouldDirty: true
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue>{formatWorkflow(watch("workflow"))}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="radiology">Radiology (NiiVue)</SelectItem>
                      <SelectItem value="pathology">Pathology (WSI)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="readers_per_case">Readers Per Case</Label>
                  <Input
                    id="readers_per_case"
                    type="number"
                    min={1}
                    max={5}
                    {...register("readers_per_case", { valueAsNumber: true })}
                  />
                </div>
                <div className="space-y-2 md:col-span-3">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input id="due_date" type="date" {...register("due_date")} />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Switch
                  checked={reviewEnabled}
                  onCheckedChange={(checked) => setValue("review_enabled", Boolean(checked), { shouldDirty: true })}
                />
                <Label className="cursor-pointer">Require review before final acceptance</Label>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Switch
                  checked={watch("multiplayer_cursors")}
                  onCheckedChange={(checked) =>
                    setValue("multiplayer_cursors", Boolean(checked), { shouldDirty: true })
                  }
                />
                <Label className="cursor-pointer">Show multiplayer cursors (live presence)</Label>
              </div>

              {mode === "queued" && (
                <div className="rounded-lg border border-dashed p-4 space-y-3">
                  <h3 className="text-sm font-semibold">Queue Settings</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="sort_metric_key">Queue Sort Field</Label>
                      <Input id="sort_metric_key" {...register("sort_metric_key")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Sort Field Type</Label>
                      <Select
                        value={watch("sort_metric_type") ?? ""}
                        onValueChange={(value) =>
                          setValue("sort_metric_type", value as "integer" | "float", {
                            shouldDirty: true
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose type">
                            {formatSortType(watch("sort_metric_type") ?? "")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="integer">Integer</SelectItem>
                          <SelectItem value="float">Float</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Queue Order</Label>
                      <Select
                        value={watch("sort_metric_direction") ?? ""}
                        onValueChange={(value) =>
                          setValue("sort_metric_direction", value as "asc" | "desc", {
                            shouldDirty: true
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose order">
                            {formatSortDirection(watch("sort_metric_direction") ?? "")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asc">Ascending</SelectItem>
                          <SelectItem value="desc">Descending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={isPending || !isDirty}>
                  {updateProject.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>

            <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/5 p-5">
              <h3 className="text-sm font-semibold text-destructive">Danger Zone</h3>
              <p className="text-sm text-muted-foreground mt-1">Irreversible actions</p>
              <Button
                type="button"
                variant="destructive"
                className="mt-4"
                disabled={archiveProject.isPending || existingProject?.status === "archived"}
                onClick={() => {
                  if (confirm("Archive this project?")) {
                    archiveProject.mutate()
                  }
                }}
              >
                {existingProject?.status === "archived"
                  ? "Project Archived"
                  : archiveProject.isPending
                    ? "Archiving..."
                    : "Archive Project"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="modules" className="mt-4">
            <ProjectFormModulesTab
              modulesLoading={modulesLoading}
              modules={modules}
              selectedModuleId={selectedModuleId}
              setSelectedModuleId={setSelectedModuleId}
              selectedModule={selectedModule}
              selectedTemplate={selectedTemplate}
              moduleTypeBadgeClass={moduleTypeBadgeClass}
              addableTemplates={addableTemplates}
              newTemplateId={newTemplateId}
              setNewTemplateId={setNewTemplateId}
              onAddModule={() => newTemplateId && addModule.mutate({ template_id: newTemplateId })}
              isAddModulePending={addModule.isPending}
              createTemplateOpen={createTemplateOpen}
              setCreateTemplateOpen={setCreateTemplateOpen}
              editTemplateOpen={editTemplateOpen}
              setEditTemplateOpen={setEditTemplateOpen}
              onCreateTemplate={(payload) => createTemplate.mutate(payload)}
              isCreateTemplatePending={createTemplate.isPending}
              createTemplateError={createTemplate.isError ? createTemplate.error.message : undefined}
              onEditTemplate={(payload) => {
                if (!selectedModule) return
                editTemplate.mutate({
                  templateId: selectedModule.template_id,
                  payload
                })
              }}
              isEditTemplatePending={editTemplate.isPending}
              editTemplateError={editTemplate.isError ? editTemplate.error.message : undefined}
              dependsOnModuleId={dependsOnModuleId}
              setDependsOnModuleId={setDependsOnModuleId}
              availableDependencyTargets={availableDependencyTargets}
              dependencyMode={dependencyMode}
              setDependencyMode={setDependencyMode}
              dependencyCardinality={dependencyCardinality}
              setDependencyCardinality={setDependencyCardinality}
              dependencyLabelFilter={dependencyLabelFilter}
              setDependencyLabelFilter={setDependencyLabelFilter}
              dependencyAutoCreate={dependencyAutoCreate}
              setDependencyAutoCreate={setDependencyAutoCreate}
              dependencyCascadeDelete={dependencyCascadeDelete}
              setDependencyCascadeDelete={setDependencyCascadeDelete}
              onAddDependencyRule={() => {
                if (!(selectedModule && dependsOnModuleId)) return
                const labels = dependencyLabelFilter
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                addDependency.mutate({
                  moduleId: selectedModule.id,
                  depends_on_module_id: dependsOnModuleId,
                  dependency_mode: dependencyMode,
                  cardinality: dependencyCardinality,
                  auto_create_linked: dependencyAutoCreate,
                  cascade_on_source_delete: dependencyCascadeDelete,
                  source_label_names: dependencyMode === "instance_linked" && labels.length > 0 ? labels : undefined
                })
              }}
              isAddDependencyPending={addDependency.isPending}
              onDeleteDependency={(depId) => {
                if (!selectedModule) return
                deleteDependency.mutate({
                  moduleId: selectedModule.id,
                  depId
                })
              }}
              isDeleteDependencyPending={deleteDependency.isPending}
              onDeleteModule={() => {
                if (!selectedModule) return
                deleteModule.mutate(selectedModule.id)
              }}
              isDeleteModulePending={deleteModule.isPending}
              onUpdateModuleConfig={(moduleId, config) => updateModuleConfig.mutate({ moduleId, config })}
              isUpdateModuleConfigPending={updateModuleConfig.isPending}
            />
          </TabsContent>

          <TabsContent value="members" className="mt-4">
            <div className="rounded-xl border bg-card/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Project Members</h3>
                {canManageMembers && availableMembers.length > 0 && (
                  <Button size="sm" onClick={() => setShowAddMember((v) => !v)}>
                    <Plus className="h-4 w-4 mr-1" /> Add Member
                  </Button>
                )}
              </div>

              {showAddMember && (
                <div className="mb-4 rounded-lg border p-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px_auto]">
                    <Select value={selectedUserId} onValueChange={(value) => setSelectedUserId(value ?? "")}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select instance member">
                          {availableMembers.find((m) => m.user_id === selectedUserId)
                            ? `${availableMembers.find((m) => m.user_id === selectedUserId)?.name} (${availableMembers.find((m) => m.user_id === selectedUserId)?.email})`
                            : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                        {availableMembers.map((m) => (
                          <SelectItem key={m.user_id} value={m.user_id}>
                            {m.name} ({m.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as ProjectRole)}>
                      <SelectTrigger>
                        <SelectValue>{formatProjectRole(selectedRole)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project_admin">Admin</SelectItem>
                        <SelectItem value="annotator">Annotator</SelectItem>
                        <SelectItem value="reviewer">Reviewer</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => {
                        if (!selectedUserId) return
                        addMember.mutate(
                          { user_id: selectedUserId, role: selectedRole },
                          {
                            onSuccess: () => {
                              setSelectedUserId("")
                              setSelectedRole("annotator")
                              setShowAddMember(false)
                            }
                          }
                        )
                      }}
                      disabled={!selectedUserId || addMember.isPending}
                    >
                      {addMember.isPending ? "Adding..." : "Add"}
                    </Button>
                  </div>
                </div>
              )}

              {membersLoading ? (
                <p className="text-sm text-muted-foreground">Loading members...</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="py-2 text-left font-medium">Name</th>
                        <th className="py-2 text-left font-medium">Email</th>
                        <th className="py-2 text-left font-medium">Role</th>
                        <th className="py-2 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectMembers.map((member) => (
                        <tr key={member.id} className="border-b border-border/60">
                          <td className="py-3 font-medium">{member.name}</td>
                          <td className="py-3 text-muted-foreground">{member.email}</td>
                          <td className="py-3">
                            {canManageMembers ? (
                              <Select
                                value={member.role}
                                onValueChange={(value) =>
                                  updateRole.mutate({ memberId: member.id, role: value as ProjectRole })
                                }
                              >
                                <SelectTrigger className="w-40" size="sm">
                                  <SelectValue>{formatProjectRole(member.role)}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="project_admin">Admin</SelectItem>
                                  <SelectItem value="annotator">Annotator</SelectItem>
                                  <SelectItem value="reviewer">Reviewer</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span>{formatProjectRole(member.role)}</span>
                            )}
                          </td>
                          <td className="py-3 text-right">
                            {canManageMembers && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Remove member"
                                onClick={() => {
                                  if (confirm("Remove this member from the project?")) {
                                    removeMember.mutate(member.id)
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-xl border bg-card/70 p-5 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name</Label>
            <Input id="name" {...register("name")} aria-invalid={Boolean(errors.name)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" rows={3} {...register("description")} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select
                value={mode}
                onValueChange={(value) => setValue("mode", value as "standard" | "queued", { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue>{formatMode(mode)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Workflow</Label>
              <Select
                value={watch("workflow")}
                onValueChange={(value) =>
                  setValue("workflow", value as "radiology" | "pathology", {
                    shouldDirty: true
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue>{formatWorkflow(watch("workflow"))}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="radiology">Radiology (NiiVue)</SelectItem>
                  <SelectItem value="pathology">Pathology (WSI)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="readers_per_case">Readers Per Case</Label>
              <Input
                id="readers_per_case"
                type="number"
                min={1}
                max={5}
                {...register("readers_per_case", { valueAsNumber: true })}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={createProject.isPending}>
              {createProject.isPending ? "Creating..." : "Create Project"}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
